import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString, WWCCIconDisplay, WWCCStyleDisplay, WWCCTextDisplay } from '../../../utils/index';

// REDUX
import { useAppSelector } from '../../../store';

// PACKAGES
import * as Yup from 'yup';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { Tooltip } from 'reactstrap';

// COMPONENTS
import Button from '../../../components/Button';
import FileUpload from '../../../components/FileUpload';
import SingleSelect from '../../../components/SingleSelect';
import FilesList from '../../../components/FilesList';
import DateDayExpiryInput from '../../../components/DateDayExpiryInput';
import StatusIcon from '../../../components/StatusIcon';
import DisplayDateTime from '../../../components/DisplayDateTime';

// ICONS
import { ReactComponent as InfoIcon } from '../../../assets/svg/Information.svg';

// TYPES
import { AccountCertificationCommand, DomainCertificationType } from '../../../api/models';
import { closeModalType } from '../../../components/GlobalModal';

interface DocumentModalTypes {
	closeModal?: closeModalType;
	formId?: string;
	setTitle?: React.Dispatch<React.SetStateAction<string>>;
	userProfileId?: string | undefined;
}

const DocumentModal: React.FC<DocumentModalTypes> = ({ closeModal, formId, setTitle, userProfileId }) => {
	// Get all States from Redux store
	const statesList = useAppSelector((state) => state?.lists.states);

	// Get all Qualification Certification from Redux store
	const qualificationCertifications = useAppSelector((state) => state?.lists.qualificationCertifications);

	// Get all Authority of Work Certifications from Redux store
	const authorityOfWorkCertifications = useAppSelector((state) => state?.lists.authorityOfWorkCertifications);

	const certificationTypes = authorityOfWorkCertifications;

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Set up Data state and Initial Data
	const [formType, setFormType] = React.useState<string>('');

	const initialFormState: AccountCertificationCommand = {
		id: undefined,
		certificationType: undefined,
		certificationNumber: '',
		state: undefined,
		date: undefined,
		expiry: undefined,
		title: '',
		organisation: '',
		userAcknowledgement: undefined,
		attachments: [],
		createAttachments: [],
		deleteAttachmentIds: []
	};

	const [formData, setFormData] = React.useState<AccountCertificationCommand>(initialFormState);

	const getDocumentData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAccountCertification(formId);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			const { result = initialFormState } = response;

			setIsLoading(false);

			const certificationType = qualificationCertifications?.filter(
				(item) => item.label === result.certificationType
			)[0]
				? 'Qualification'
				: result.certificationType;

			setFormType(certificationType || '');

			setFormData((prevState) => ({
				...prevState,
				...response.result
			}));
		}
	};

	React.useEffect(() => {
		if (formId) {
			getDocumentData();
		}
		setIsLoading(false);
	}, []);

	// Validation
	const formValidationSchema = Yup.object().shape({
		state: Yup.string().when('certificationType', {
			is: (value: string) =>
				value === DomainCertificationType.TeacherRegistration ||
				value === DomainCertificationType.WorkingWithChildrenCheck,
			then: Yup.string().required('Required')
		}),
		certificationNumber: Yup.string().when('certificationType', {
			is: (value: string) =>
				value === DomainCertificationType.TeacherRegistration ||
				value === DomainCertificationType.WorkingWithChildrenCheck,
			then: Yup.string().required('Required')
		}),
		expiry: Yup.string().when('certificationType', {
			is: (value: string) =>
				value === DomainCertificationType.TeacherRegistration ||
				value === DomainCertificationType.WorkingWithChildrenCheck,
			then: Yup.string().required('Required')
		}),
		date: Yup.string().when('certificationType', {
			is: (value: string) =>
				value === DomainCertificationType.Degree ||
				value === DomainCertificationType.Diploma ||
				value === DomainCertificationType.Certificate ||
				value === DomainCertificationType.PoliceCheck,
			then: Yup.string().required('Required')
		}),
		title: Yup.string().when('certificationType', {
			is: (value: string) =>
				value === DomainCertificationType.Degree ||
				value === DomainCertificationType.Diploma ||
				value === DomainCertificationType.Certificate,
			then: Yup.string().required('Required')
		}),
		organisation: Yup.string().when('certificationType', {
			is: (value: string) =>
				value === DomainCertificationType.Degree ||
				value === DomainCertificationType.Diploma ||
				value === DomainCertificationType.Certificate,
			then: Yup.string().required('Required')
		}),
		certificationType: Yup.string().required('Required'),
		createAttachments: Yup.array().when(['attachments', 'deleteAttachmentIds'], {
			is: (attachments: any[], deletedItems: any[]) => {
				if (attachments?.length === 1 && deletedItems?.length > 0) {
					if (deletedItems.includes(attachments[0].id)) {
						return true;
					} else {
						return false;
					}
				} else if (attachments?.length === 0) {
					return true;
				}
				return false;
			},
			then: Yup.array().min(1, 'Required')
		}),

		userAcknowledgement: Yup.boolean().when('certificationType', {
			is: (value: string) =>
				value === DomainCertificationType.TeacherRegistration ||
				value === DomainCertificationType.WorkingWithChildrenCheck ||
				value === 'VaccinationCertificate',
			then: Yup.boolean().oneOf([true], 'You must agree to continue').required('Required')
		})
	});

	//Submit form data to API
	const submitFormData = async (values: any) => {
		setIsLoading(true);

		const data: AccountCertificationCommand = {
			id: formId ? formId : '',
			certificationType: values.certificationType as DomainCertificationType,
			certificationNumber: values.certificationNumber,
			state: values.state,
			title: values.title,
			organisation: values.organisation,
			date: values.date,
			expiry: values.expiry,
			userAcknowledgement: values.userAcknowledgement,
			createAttachments: values.createAttachments.filter((id: string) => values.attachments.id !== id),
			attachments: values.attachments,
			deleteAttachmentIds: values.deleteAttachmentIds
		};

		const response = await apibridge.addCertification(data);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			if (closeModal) {
				closeModal();
			}
		}
	};

	// ===== WWCC API Logic =====
	const [adminTooltipOpen, setAdminTooltipOpen] = React.useState<boolean>(false);
	const toggleAdminTooltip = () => setAdminTooltipOpen(!adminTooltipOpen);

	return (
		<div className="document-form-modal">
			<Formik
				enableReinitialize
				initialValues={formData}
				validationSchema={formValidationSchema}
				onSubmit={submitFormData}
			>
				{(formik) => {
					return (
						<Form translate="yes">
							{!formId && !formType ? (
								<>
									<p className="mbtom-30">Which document do you wish to add?</p>

									{certificationTypes?.map((cert, i) => (
										<Button
											key={i}
											onClick={() => {
												setFormType(cert.value!),
													setTitle!(cert.label!),
													formik.setFieldValue('certificationType', cert.value);
											}}
											className="btn btn-secondary mbtom-20 full-width"
										>
											{cert.label}
										</Button>
									))}
									<Button
										onClick={() => {
											setFormType('Qualification'), setTitle!('Qualification');
										}}
										className="btn btn-secondary mbtom-20 full-width"
									>
										Qualification
									</Button>
									<Button
										onClick={() => {
											setFormType('VaccinationCertificate'),
												setTitle!('Vaccination'),
												formik.setFieldValue('certificationType', 'VaccinationCertificate');
										}}
										className="btn btn-secondary mbtom-20 full-width"
									>
										Vaccination
									</Button>
									<Button
										onClick={() => {
											setFormType('PoliceCheck'),
												setTitle!('Police Check'),
												formik.setFieldValue('certificationType', 'PoliceCheck');
										}}
										className="btn btn-secondary mbtom-20 full-width"
									>
										Police Check
									</Button>
								</>
							) : (
								<>
									{formType === 'PoliceCheck' ? (
										<>
											<div className="row">
												<div className={`form-group col-12 `}>
													<label htmlFor="date">Date of police check</label>

													<Field
														name="date"
														children={() => {
															return (
																<DateDayExpiryInput
																	dateTimeString={formik.values.date || ''}
																	onChange={(val: any) => formik.setFieldValue('date', val)}
																	showDropdowns={false}
																	excludeFuture={true}
																/>
															);
														}}
													/>

													<ErrorMessage component={'div'} name="date" className="error-message" />
												</div>
											</div>
										</>
									) : formType === 'TeacherRegistration' || formType === 'WorkingWithChildrenCheck' ? (
										<>
											<div className="form-group two-col">
												<div className="sub-form-group">
													<label htmlFor="state">State or Territory</label>

													<Field
														children={() => {
															return (
																<>
																	<SingleSelect
																		name="state"
																		options={statesList}
																		value={formik.values.state}
																		onChange={(val: any) => formik.setFieldValue('state', val)}
																	/>
																</>
															);
														}}
													/>

													<ErrorMessage component={'div'} name="state" className="error-message" />
												</div>
												<div className="sub-form-group">
													<label htmlFor={'expiry'}>Expiry</label>

													<Field
														className="form-control"
														name={'expiry'}
														children={() => (
															<DateDayExpiryInput
																dateTimeString={formik.values.expiry || ''}
																onChange={(val: any) => formik.setFieldValue('expiry', val)}
																excludePast={true}
															/>
														)}
													/>

													<ErrorMessage name={'expiry'}>
														{(error) => <div className="form-validation-error">{error}</div>}
													</ErrorMessage>
												</div>
											</div>
											<div className="form-group">
												<label htmlFor={`certificationNumber`}>Registration number</label>

												<Field
													name={`certificationNumber`}
													className="form-control"
													type="text"
													placeholder=""
													onChange={(val: any) => {
														formik.setFieldValue(`certificationNumber`, val.target.value);
													}}
													value={formik.values.certificationNumber}
												/>

												<ErrorMessage name={`certificationNumber`}>
													{(error) => <div className="form-validation-error">{error}</div>}
												</ErrorMessage>
											</div>

											{formType === 'WorkingWithChildrenCheck' && formData.wwccCanDisplaySingleStatusCheck ? (
												<>
													<div className="general-divider"></div>

													<div className="row">
														<div className="col-4">
															<div className="d-flex align-items-center fw-medium text-grey-4 mb-2">Status</div>
														</div>

														<div className="col-8">
															<div className="d-flex gap-2 align-items-center justify-content-start mb-2">
																<StatusIcon
																	statusType={WWCCIconDisplay(formData.wwccEligibleToWork)}
																	width={24}
																	height={24}
																/>

																<p className={`mb-0 fw-medium ${WWCCStyleDisplay(formData.wwccEligibleToWork)}`}>
																	Eligible to work: {WWCCTextDisplay(formData.wwccEligibleToWork)}
																</p>

																{formData.wwccMessage ? (
																	<>
																		<InfoIcon className="info-icon mt-0" id="wwcc-message-tooltip" />

																		<Tooltip
																			placement="top"
																			isOpen={adminTooltipOpen}
																			autohide={false}
																			target="wwcc-message-tooltip"
																			toggle={toggleAdminTooltip}
																		>
																			<div className="tooltip-content">{formData.wwccMessage}</div>
																		</Tooltip>
																	</>
																) : null}
															</div>
														</div>

														<div className="col-4">
															<div className="d-flex align-items-center h-100 fw-medium text-grey-4">Last check</div>
														</div>

														<div className="col-4 pr-0">
															<div className="d-flex align-items-center h-100">
																<span className={`fw-medium ${WWCCStyleDisplay(formData.wwccEligibleToWork)}`}>
																	{formData.wwccLastChecked ? (
																		<DisplayDateTime format="DD/MM/YYYY, hh:mma">
																			{formData.wwccLastChecked}
																		</DisplayDateTime>
																	) : (
																		<>Not checked yet</>
																	)}
																</span>
															</div>
														</div>
													</div>

													<div className="general-divider"></div>
												</>
											) : null}
										</>
									) : null}

									{formType === 'Qualification' ? (
										<>
											<div className="form-group">
												<label htmlFor="certificationType">Qualification</label>

												<Field
													children={() => {
														return (
															<SingleSelect
																name="certificationType"
																onChange={(val: string) => {
																	formik.setFieldValue('certificationType', val);
																}}
																options={qualificationCertifications}
																value={formik.values.certificationType}
																placeholder="Select qualification..."
															/>
														);
													}}
												/>

												<ErrorMessage component={'div'} name="certificationType" className="error-message" />
											</div>
											<div className="form-group">
												<label htmlFor={`title`}>Title of qualification</label>

												<Field
													name={`title`}
													className="form-control"
													type="text"
													placeholder=""
													onChange={(val: any) => {
														formik.setFieldValue(`title`, val.target.value);
													}}
													value={formik.values.title}
												/>

												<ErrorMessage name={`title`}>
													{(error) => <div className="form-validation-error">{error}</div>}
												</ErrorMessage>
											</div>
											<div className="form-group">
												<label htmlFor={`date`}>Date</label>

												<DateDayExpiryInput
													showDropdowns={true}
													excludeFuture={true}
													dateTimeString={formik.values.date || ''}
													onChange={(val: string) => {
														formik.setFieldValue(`date`, val);
													}}
												/>
												<ErrorMessage name={`date`}>
													{(error) => <div className="form-validation-error">{error}</div>}
												</ErrorMessage>
											</div>

											<div className="form-group">
												<label htmlFor={`organisation`}>Organisation obtained from</label>

												<Field
													name={`organisation`}
													className="form-control"
													type="text"
													placeholder=""
													onChange={(val: any) => {
														formik.setFieldValue(`organisation`, val.target.value);
													}}
													value={formik.values.organisation}
												/>

												<ErrorMessage name={`organisation`}>
													{(error) => <div className="form-validation-error">{error}</div>}
												</ErrorMessage>
											</div>
										</>
									) : null}

									{formType === 'VaccinationCertificate' ? (
										<>
											<div className="form-group">
												<p>You may choose to provide evidence of vaccination status.</p>
											</div>
										</>
									) : null}

									<div className="form-group">
										<Field
											children={() => {
												return (
													<>
														<label htmlFor="createAttachments">
															{formType === 'VaccinationCertificate' || 'Qualification'
																? 'Attachment'
																: 'Photo of registration (optional)'}
														</label>

														<FilesList
															files={formik.values.attachments?.map((file) => ({
																id: file.id,
																displayFileName: file.fileName,
																link: file.link
															}))}
															onChange={(val: string[]) => {
																formik.setFieldValue('deleteAttachmentIds', [val]);
															}}
														/>

														<FileUpload
															name="createAttachments"
															placeholder="Choose file..."
															onChange={(val: any[], files: any[]) => {
																return formik.setFieldValue('createAttachments', val);
															}}
															multiple={true}
														/>
													</>
												);
											}}
										/>

										<ErrorMessage component={'div'} name="createAttachments" className="error-message" />
									</div>

									{formType === 'TeacherRegistration' ? (
										<div className="form-group acknowledge-container ">
											<label className="form-control-checkbox">
												<Field
													name="userAcknowledgement"
													type="checkbox"
													onChange={() =>
														formik.setFieldValue(`userAcknowledgement`, !formik.values.userAcknowledgement)
													}
													checked={formik.values.userAcknowledgement}
												/>

												<span>
													I have notified the teacher registration authority that I am working or volunteering at this
													organisation
												</span>
											</label>

											<ErrorMessage name={`userAcknowledgement`}>
												{(error) => <div className="form-validation-error">{error}</div>}
											</ErrorMessage>
										</div>
									) : formType === 'WorkingWithChildrenCheck' ? (
										<div className="form-group acknowledge-container">
											<label className="form-control-checkbox">
												<Field
													name="userAcknowledgement"
													type="checkbox"
													onChange={() =>
														formik.setFieldValue(`userAcknowledgement`, !formik.values.userAcknowledgement)
													}
													checked={formik.values.userAcknowledgement}
												/>

												<span>
													I have notified the Working with Children Check authority that I am working or volunteering at
													this organisation.
												</span>
											</label>

											<ErrorMessage name={`userAcknowledgement`}>
												{(error) => <div className="form-validation-error">{error}</div>}
											</ErrorMessage>
										</div>
									) : formType === 'VaccinationCertificate' ? (
										<div className="form-group acknowledge-container">
											<label className="form-control-checkbox">
												<Field name="userAcknowledgement" type="checkbox" onChange={formik.handleChange} />

												<span>
													I acknowledge that I am up to date with all vaccinations required by my organisation and by
													law (unless a medical exemption applies).
												</span>
											</label>

											<ErrorMessage name="userAcknowledgement">
												{(error) => <div className="form-validation-error">{error}</div>}
											</ErrorMessage>
										</div>
									) : null}

									<Button disabled={isLoading} type="submit" className="btn-primary full-width">
										Save and Close
									</Button>
								</>
							)}
						</Form>
					);
				}}
			</Formik>
		</div>
	);
};

export default DocumentModal;
